import posthog from "posthog-js";
import { sendGTMEvent } from "@next/third-parties/google";

function readGaClientId(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  // _ga cookie value looks like "GA1.1.123456789.1700000000".
  // Convention is to keep the last two segments as the client_id.
  const parts = raw.split(".");
  if (parts.length >= 4) return parts.slice(-2).join(".");
  return raw;
}

function readPosthogDistinctId(): string | null {
  try {
    const id = posthog.get_distinct_id?.();
    return typeof id === "string" && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

export function getTiktokShareIdentifiers(): {
  ga_client_id: string | null;
  posthog_distinct_id: string | null;
} {
  return {
    ga_client_id: readGaClientId(),
    posthog_distinct_id: readPosthogDistinctId()
  };
}

export async function mintTiktokShareCode(): Promise<string | null> {
  try {
    const res = await fetch("/api/tiktok-share-attribution", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(getTiktokShareIdentifiers())
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { share_code?: string };
    return data.share_code ?? null;
  } catch {
    return null;
  }
}

async function sha256Hex(input: string): Promise<string | null> {
  try {
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(input.toLowerCase().trim())
    );
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

// Fires a TikTok-demo-specific GTM event with `external_id = sha256(_ga)`
// so the TikTok pixel binds the anonymous student's `_ttp` to that
// external_id. The parent's later payment event carries the same external_id
// (server-side and client-side) so TikTok can stitch the parent's conversion
// back to this student's ad click.
//
// Deliberately uses a name distinct from the existing `share_invite_whatsapp`
// event (which fires from the logged-in student→parent invite flow) so the
// two share contexts don't conflate.
export async function fireTiktokDemoShareEvent(): Promise<void> {
  const gaClientId = readGaClientId();
  if (!gaClientId) return;
  const hash = await sha256Hex(gaClientId);
  if (!hash) return;
  sendGTMEvent({
    event: "tiktok_demo_share",
    external_id: hash
  });
}

const TIKTOK_SHARECODE_COOKIE_NAME = "wiswiz_tiktok_sharecode";

// Set when a parent (or anyone) lands on the demo with a sharecode; read by
// the frontend post-login to call /claim, linking parent.user_id to the share
// row so parent-payment attribution can look it up. 24-hour lifetime is long
// enough to cover demo → signup, short enough to expire well before a stale
// cookie could pollute an unrelated future signup.
export function setTiktokSharecodeCookie(shareCode: string): void {
  if (typeof document === "undefined") return;
  const oneDay = 60 * 60 * 24;
  document.cookie =
    `${TIKTOK_SHARECODE_COOKIE_NAME}=${encodeURIComponent(shareCode)};` +
    `Max-Age=${oneDay};Path=/;SameSite=Lax`;
}

function readTiktokSharecodeCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${TIKTOK_SHARECODE_COOKIE_NAME}=([^;]+)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function clearTiktokSharecodeCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie =
    `${TIKTOK_SHARECODE_COOKIE_NAME}=;Max-Age=0;Path=/;SameSite=Lax`;
}

// Claim the share row for the now-authenticated parent. Called post-identify
// in AuthContext; idempotent on the backend, so safe to call on every
// identify. Clears the cookie after the call so it's a one-shot per landing.
export async function claimTiktokShareForCurrentUser(): Promise<void> {
  const shareCode = readTiktokSharecodeCookie();
  if (!shareCode) return;
  try {
    await fetch(
      `/api/tiktok-share-attribution/${encodeURIComponent(shareCode)}/claim`,
      { method: "POST" }
    );
  } catch {
    // best-effort
  } finally {
    clearTiktokSharecodeCookie();
  }
}

export async function recordTiktokShareVisit(shareCode: string): Promise<void> {
  try {
    await fetch(
      `/api/tiktok-share-attribution/${encodeURIComponent(shareCode)}/visit`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(getTiktokShareIdentifiers())
      }
    );
  } catch {
    // Attribution is best-effort; never block UI.
  }
}
